import { EngineRuleRoomRate, PaymentOption } from '@niarab2c/frontend-commons/src/types/hotels';
import { formatDate, testSessionStorage } from '@niaratech/niara-js-commons';
import { diffInDays } from '@niaratech/niara-js-commons/dist/dateUtils';
import { useImmutableFunction } from '@niaratech/niara-react-components';
import { useHotelSearchContext, type HotelCartProps } from '@niaratech/ota-components/src';
import { AddonCartItem } from '@niaratech/ota-components/src/components';
import _pick from 'lodash/pick';
import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { CriteriaForm, QuotationRoomRates, ShoppingCart, useDispatch, useSelector } from '../app/base';
import { generateQueryString } from '../util/hotels';
import { EXPECTED_ROOMS_COUNT_KEY } from '../app/reducers/hotel/hotelSearch';
import { validateAndGenerateSlotTitle } from './useRoomOptions';
const SESSION_STORAGE_AVAILABLE = testSessionStorage();
interface UseCartPropsParams {
  isTemporaryItems?: boolean;
}
type UseCartReturn = Pick<HotelCartProps, 'cartItems' | 'cartTotal' | 'paymentOptions' | 'onCreateQuotation' | 'onGoToCart'> & {
  addonCartItems: AddonCartItem[];
  addonsPaymentOptions: PaymentOption[];
  isOpenCartModal?: boolean;
  setIsOpenCartModal?: (isOpen: boolean) => void;
  onAddMoreRooms?: (criteria: CriteriaForm) => void;
  expectedRoomsCount?: number;
  combinedPaymentOptions: PaymentOption[]; // junção do paymentOptions com o addonPaymentOptions
};
const useCartProps = ({
  isTemporaryItems = false
}: UseCartPropsParams = {}): UseCartReturn => {
  const dispatch = useDispatch();
  const {
    hotelsPath,
    cartPath
  } = useHotelSearchContext();
  const history = useHistory();
  const {
    shoppingCartAddonItems,
    shoppingCartItemsWithoutQuantity,
    shoppingCartItems,
    shoppingCartTotal,
    person,
    b2c,
    storefront,
    user,
    buyerPersonalData
  } = useSelector(s => ({
    shoppingCartAddonItems: s.shoppingCart?.addonCartItems,
    shoppingCartItems: isTemporaryItems ? s.shoppingCart?.temporaryItemsWithQuantity : s.shoppingCart?.itemsWithQuantity,
    shoppingCartItemsWithoutQuantity: isTemporaryItems ? s.shoppingCart?.temporaryItems : s.shoppingCart?.items,
    shoppingCartTotal: isTemporaryItems ? s.shoppingCart?.temporaryTotal : s.shoppingCart?.total,
    person: s.shoppingCart?.person,
    b2c: s.authentication?.b2c,
    storefront: s?.storefrontConfig?.storefront,
    user: s.authentication?.user,
    buyerPersonalData: s.shoppingCart?.personalData?.buyer
  }));
  const addonCartItems: AddonCartItem[] = useMemo(() => {
    return shoppingCartAddonItems?.map(item => ({
      ...item,
      onRemove: () => {
        dispatch(ShoppingCart.removeAddonCartItem({
          itemId: item?.itemId
        }));
      }
    }));
  }, [shoppingCartAddonItems, dispatch]);
  const cartItems: HotelCartProps['cartItems'] = React.useMemo(() => shoppingCartItems?.map(item => {
    const roomImage = item?.roomRates?.[0]?.roomType?.image;
    const price = item.roomRates?.[0]?.priceComposition.productPrice;
    const taxes = item.roomRates?.[0]?.priceComposition.taxes.value;
    const total = {
      ...price,
      value: price.value * item.quantity
    };
    const roomRate = item.roomRates[0];
    const {
      occupancy
    } = item;
    const selectedSlotIndex = item?.loyaltyOptInRoomRateId?.optInId === roomRate?.id ? roomRate?.metasearch?.slots?.findIndex(slot => slot?.ctaAction === item?.loyaltyOptInRoomRateId?.optInCtaAction) : item?.loyaltyOptInRoomRateId?.originalCtaAction === roomRate?.id ? roomRate?.metasearch?.slots?.findIndex(slot => slot?.ctaAction === item?.loyaltyOptInRoomRateId?.originalCtaAction) : roomRate?.metasearch?.slots?.findIndex(slot => slot?.ctaAction === 'ADD_TO_CART');
    const badgeHighlighted = selectedSlotIndex == 0;
    const buyerInfo = buyerPersonalData || user;
    return {
      hotelName: item.hotelResult?.hotel.name,
      hotelImage: item?.hotelResult?.hotel?.extra?.thumbnail,
      cityName: item.hotelResult?.hotel?.cityName,
      stateName: item.hotelResult?.hotel?.stateName,
      roomTitle: item.roomRates?.[0].roomType?.name,
      roomDescr: item.roomRates?.[0].descriptions.roomType?.[0]?.description,
      roomImage: roomImage,
      policy: item.roomRates?.[0].cancelPolicy,
      guests: occupancy?.adults ?? 0,
      nights: diffInDays(item.endDate, item.startDate) ?? 1,
      currency: price.currency,
      ...validateAndGenerateSlotTitle(roomRate.metasearch?.slots, roomRate.metasearch?.slots[selectedSlotIndex]),
      badgeHighlighted,
      promotion: roomRate.promotion,
      taxes,
      totalValue: total.value,
      children: occupancy?.children ?? 0,
      childrenAges: occupancy?.childrenAges ?? [],
      onRemove: () => {
        dispatch(ShoppingCart.removeItem({
          item,
          isTemporaryItems,
          buyer: buyerInfo,
          storefront
        }));
      },
      hotelId: item?.hotelId,
      startDate: item?.startDate,
      endDate: item?.endDate,
      roomRates: item?.roomRates,
      criteria: item?.criteria,
      quantity: item.quantity,
      itemId: item?.itemId,
      itemIds: shoppingCartItemsWithoutQuantity?.filter(ci => ci?.roomRates?.[0]?.id === roomRate?.id)?.map(ci => ci?.itemId)
    };
  }) as HotelCartProps['cartItems'], [buyerPersonalData, dispatch, isTemporaryItems, shoppingCartItems, shoppingCartItemsWithoutQuantity, storefront, user]);
  const firstRoomRate = shoppingCartItems?.[0]?.roomRates?.[0];
  const paymentOptions = React.useMemo(() => {
    // TODO: quase bug - hj pega apenas as opções de pagamento do primeiro quarto. Só não é bug pq não permitimos
    // adicionar itens com diferentes formas de pagamento. Mas isso vai mudar.
    if (!firstRoomRate) return [];
    if (firstRoomRate.engineRuleVersion !== '1') return firstRoomRate.payment?.paymentOptions;
    const firstPaymentOptions = firstRoomRate.paymentOptions as PaymentOption[];
    const _paymentOptions = firstPaymentOptions?.map(paymentOption => {
      const totalByPaymentOption = shoppingCartItems?.reduce((acc, item) => {
        const itemRoomRate = item.roomRates?.[0] as EngineRuleRoomRate;
        const itemPaymentOption = itemRoomRate?.paymentOptions?.find(p => p.id === paymentOption.id) as PaymentOption;
        return [acc[0] + (itemPaymentOption?.paymentValue || 0), acc[1] + (itemPaymentOption?.actualTotalValue || 0), acc[2] + (itemPaymentOption?.priceComposition?.total?.value || 0)];
      }, [0, 0, 0] // paymentValue, actualTotalValue, priceCompositionTotalValue
      );
      return {
        ...paymentOption,
        paymentValue: totalByPaymentOption[0],
        actualTotalValue: totalByPaymentOption[1],
        priceComposition: {
          ...paymentOption.priceComposition,
          total: {
            ...paymentOption.priceComposition?.total,
            value: totalByPaymentOption[2]
          }
        }
      };
    });
    return _paymentOptions;
  }, [firstRoomRate, shoppingCartItems]);
  const addonsCommonPaymentOptions = React.useMemo(() => {
    if (!shoppingCartAddonItems?.length) return [];
    const paymentOptionsPerItem = shoppingCartAddonItems?.map(item => ({
      paymentOptions: item?.addonResult?.paymentOptions || [],
      priceComposition: item?.addonResult?.priceComposition
    }));

    // Agrupar opções de pagamento pelo paymentOption.id
    const groupedPaymentOptions: Record<string, Array<{
      paymentOption: PaymentOption;
      priceComposition: any;
    }>> = {};
    paymentOptionsPerItem?.forEach(item => {
      item.paymentOptions?.forEach(paymentOption => {
        if (!groupedPaymentOptions[paymentOption?.id]) {
          groupedPaymentOptions[paymentOption?.id] = [];
        }
        groupedPaymentOptions[paymentOption?.id].push({
          paymentOption,
          priceComposition: item.priceComposition
        });
      });
    });

    // Filtrar apenas opções comuns a todos os addons e acumular valores
    return Object.values(groupedPaymentOptions).filter(payments => payments?.length === shoppingCartAddonItems?.length).map(payments => {
      const firstPayment = payments[0].paymentOption;
      return payments.reduce((acc, {
        paymentOption,
        priceComposition
      }) => ({
        ...acc,
        paymentValue: (acc?.paymentValue || 0) + (paymentOption?.paymentValue || 0),
        actualTotalValue: (acc?.actualTotalValue || 0) + (paymentOption?.actualTotalValue || 0),
        priceComposition: {
          ...priceComposition,
          total: {
            value: (acc?.priceComposition?.total?.value || 0) + (priceComposition?.total?.value || 0),
            currency: priceComposition?.total?.currency || acc?.priceComposition?.total?.currency || 'BRL'
          }
        }
      }), {
        ...firstPayment,
        paymentValue: 0,
        actualTotalValue: 0,
        priceComposition: {
          total: {
            value: 0,
            currency: 'BRL'
          }
        }
      });
    });
  }, [shoppingCartAddonItems]);
  const combinedPaymentOptions = React.useMemo(() => {
    const allOptions = [...(paymentOptions || []), ...(addonsCommonPaymentOptions || [])];
    const groupedOptions: Record<string, PaymentOption[]> = {};
    allOptions.forEach(option => {
      if (!groupedOptions[option?.id]) {
        groupedOptions[option?.id] = [];
      }
      groupedOptions[option?.id].push(option);
    });
    return Object.values(groupedOptions).map(options => options.reduce((acc, option) => ({
      ...acc,
      paymentValue: (acc?.paymentValue || 0) + (option?.paymentValue || 0),
      actualTotalValue: (acc?.actualTotalValue || 0) + (option?.actualTotalValue || 0),
      priceComposition: {
        ...acc.priceComposition,
        total: {
          ...acc.priceComposition?.total,
          value: (acc?.priceComposition?.total?.value || 0) + (option?.priceComposition?.total?.value || 0)
        }
      }
    })));
  }, [paymentOptions, addonsCommonPaymentOptions]);
  const onCreateQuotation: HotelCartProps['onCreateQuotation'] = useImmutableFunction(b2c ? undefined : () => {
    shoppingCartItems.map<QuotationRoomRates.MailableResult>(shoppingCartItem => {
      const roomRate = shoppingCartItem.roomRates?.[0];
      const hotelResult = shoppingCartItem.hotelResult;
      if (!roomRate || !hotelResult) return undefined;
      const {
        startDate,
        endDate
      } = roomRate.time;
      const mailableResult: QuotationRoomRates.MailableResult = {
        id: roomRate.id,
        label: `${hotelResult.hotel.name} ${formatDate(startDate, undefined, 'dd/MM')}-${formatDate(endDate, undefined, 'dd/MM')}`,
        result: {
          hotel: hotelResult.hotel,
          _id: hotelResult._id
        },
        engineRuleVersion: roomRate.engineRuleVersion,
        roomRates: [roomRate],
        criteria: {
          time: {
            startDate,
            endDate
          },
          occupancy: {
            ..._pick(roomRate?.occupancy, 'adults', 'children', 'childrenAges'),
            quantity: 1
          },
          clientId: hotelResult.clientId,
          destinationName: hotelResult.hotel?.cityName,
          destinations: {
            cityIds: [hotelResult.hotel.cityId]
          }
        },
        person: {
          personId: person?.id,
          personName: [person?.firstName, person?.lastName].filter(Boolean).join(' ')
        }
      };
      return mailableResult;
    }).filter(Boolean).forEach(result => {
      dispatch(QuotationRoomRates.rehydrateAndAdd({
        result,
        openModal: true
      }));
    });
  });
  const [isOpenCartModal, setIsOpenCartModal] = React.useState(false);
  useEffect(() => {
    setIsOpenCartModal(isOpenCartModal);
  }, [isOpenCartModal]);
  const onAddMoreRooms = criteria => {
    if (criteria.destinations?.hotelIds?.length === 1) {
      history?.push(`${hotelsPath}/${criteria.destinations?.hotelIds[0]}#${generateQueryString(criteria)}`);
    } else {
      history?.push(`${hotelsPath}/search#${generateQueryString(criteria)}`);
    }
  };
  const onGoToCart = React.useCallback(() => {
    // redireciona para o carrinho
    history.push(cartPath);
  }, [history, cartPath]);
  const expectedRoomsCount = SESSION_STORAGE_AVAILABLE ? Number(sessionStorage.getItem(EXPECTED_ROOMS_COUNT_KEY) ?? 1) : undefined;
  return {
    cartItems,
    addonCartItems,
    cartTotal: shoppingCartTotal,
    paymentOptions,
    addonsPaymentOptions: addonsCommonPaymentOptions,
    isOpenCartModal,
    setIsOpenCartModal,
    onCreateQuotation: b2c ? undefined : onCreateQuotation,
    onAddMoreRooms,
    onGoToCart: b2c ? onGoToCart : undefined,
    expectedRoomsCount,
    combinedPaymentOptions
  };
};
export default useCartProps;